import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useState, useEffect } from 'react'
import Cropper, { Area } from 'react-easy-crop'
import { usePolicy } from '../contexts/PolicyContext'
import Asset from '../types/Asset'
import Tooltip from '@mui/material/Tooltip'
import Box from '@mui/material/Box'
import Button, { ButtonProps } from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Slider from '@mui/material/Slider'
import { LoadingButton } from '@mui/lab'
import Dropzone, { DropzoneProps } from './Dropzone'
import Upload from '@mui/icons-material/Upload'
import useDialog from '../hooks/useDialog'
import { createErrorNotification } from '../redux/slices/notifications'
import { getCroppedImageBlob, prepareImageForCrop, reduceImageFileSize } from '../utils/imageCrop'
import { uploadAssetPhoto } from '../services/assetService'
import { Photo } from '@mui/icons-material'

const ASSET_PHOTO_UPLOAD_MAX_BYTES = 5 * 1024 * 1024
const ASSET_PHOTO_UPLOAD_TARGET_BYTES = 4.8 * 1024 * 1024

interface AssetPhotoUploadButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  asset: Asset
  component?: typeof IconButton | typeof Button
  onUpdate?: (asset: Asset) => void
}

export default function AssetPhotoUploadButton({
  asset,
  component = IconButton,
  onUpdate,
  variant = 'outlined',
  ...props
}: AssetPhotoUploadButtonProps) {
  const { t } = useTranslation('asset')

  const dispatch = useDispatch()

  const { canEditAsset } = usePolicy()

  const { open, openDialog, closeDialog } = useDialog()

  const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false)

  const [pendingFile, setPendingFile] = useState<File | null>(null)

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const [zoom, setZoom] = useState(1)

  const [crop, setCrop] = useState({ x: 0, y: 0 })

  const [cropMode, setCropMode] = useState<'normal' | 'square'>('normal')

  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  const [imageAspect, setImageAspect] = useState<number>(1)

  async function handlePhotoFile(newFile: File): Promise<void> {
    if (!newFile.type.startsWith('image/')) {
      return
    }

    let fileForCrop
    try {
      fileForCrop = await prepareImageForCrop(newFile)
    } catch (_error) {
      fileForCrop = newFile
    }

    setPendingFile(fileForCrop)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    setCropMode('normal')

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }

    setPreviewUrl(URL.createObjectURL(fileForCrop))
  }

  const handlePhotoDrop: DropzoneProps['onDrop'] = async (acceptedFiles): Promise<void> => {
    const newFile = acceptedFiles?.[0]
    if (newFile) {
      await handlePhotoFile(newFile)
    }
  }

  function handleCropModeChange(_event: unknown, mode: 'normal' | 'square' | null): void {
    if (mode === null) {
      return
    }

    setCropMode(mode)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
  }

  const handleCropComplete = (_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels)
  }

  /**
   * Upload photo.
   */
  async function uploadPhoto(): Promise<void> {
    try {
      setIsUploadingPhoto(true)

      const currentFile = pendingFile
      if (currentFile !== null) {
        let uploadFile = currentFile
        if (previewUrl && croppedAreaPixels) {
          const blob = await getCroppedImageBlob(previewUrl, croppedAreaPixels, currentFile.type)
          uploadFile = new File([blob], currentFile.name, {
            type: currentFile.type,
            lastModified: Date.now()
          })
        }

        uploadFile = await reduceImageFileSize(uploadFile, ASSET_PHOTO_UPLOAD_TARGET_BYTES)

        if (uploadFile.size > ASSET_PHOTO_UPLOAD_MAX_BYTES) {
          throw new Error('FILE_TOO_LARGE')
        }

        onUpdate?.(await uploadAssetPhoto(asset.id, uploadFile))
      }

      closeDialog()
    } catch (error) {
      if (error instanceof Error && error.message === 'FILE_TOO_LARGE') {
        // FIXME: Translate.
        dispatch(createErrorNotification('Photo is too large. Please choose a smaller image.'))
      } else {
        // FIXME: Translate.
        dispatch(createErrorNotification('Failed to upload photo!'))
      }
    } finally {
      setIsUploadingPhoto(false)
    }
  }

  function handleClose(): void {
    setPendingFile(null)

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setPreviewUrl(null)

    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    setCropMode('normal')
    setImageAspect(1)

    closeDialog()
  }

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  useEffect(() => {
    if (!previewUrl) {
      setImageAspect(1)
      return
    }

    let cancelled = false
    const image = new Image()

    image.onload = () => {
      if (!cancelled && image.naturalWidth > 0 && image.naturalHeight > 0) {
        setImageAspect(image.naturalWidth / image.naturalHeight)
      }
    }

    image.src = previewUrl

    return () => {
      cancelled = true
    }
  }, [previewUrl])

  useEffect(() => {
    if (asset.photoUrl === undefined || asset.photoUrl === null) {
      setPreviewUrl(null)
    }
  }, [asset.photoUrl])

  if (!canEditAsset(asset)) {
    return null
  }

  return (
    <>
      {component === IconButton ? (
        <Tooltip title={t('uploadNewPhoto')}>
          <IconButton size="small" onClick={openDialog} {...props}>
            <Upload />
          </IconButton>
        </Tooltip>
      ) : (
        <Button onClick={openDialog} variant={variant} {...props}>
          {t('addPhoto')}
        </Button>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        {previewUrl === null ? (
          <>
            <DialogTitle>{t('uploadNewPhoto')}</DialogTitle>
            <DialogContent>
              <Dropzone
                icon={<Photo color="disabled" sx={{ fontSize: 48 }} />}
                label={t('uploadNewPhoto')}
                minHeight={150}
                height="auto"
                onDrop={handlePhotoDrop}
              />
            </DialogContent>
            <DialogActions>
              <Button color="inherit" onClick={handleClose}>
                {t('actions:cancel')}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>{t('previewPhoto')}</DialogTitle>
            <DialogContent>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 320,
                  bgcolor: 'grey.900',
                  borderRadius: 2,
                  overflow: 'hidden'
                }}
              >
                {previewUrl && (
                  <Cropper
                    image={previewUrl}
                    crop={crop}
                    zoom={zoom}
                    aspect={cropMode === 'square' ? 1 : imageAspect}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={handleCropComplete}
                  />
                )}
              </Box>
              <Box sx={{ mt: 3, px: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('common:crop')}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={cropMode}
                  onChange={handleCropModeChange}
                >
                  <ToggleButton value="normal">{t('common:normal')}</ToggleButton>
                  <ToggleButton value="square">{t('common:square')}</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box sx={{ mt: 3, px: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('common:zoom')}
                </Typography>
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(_, value) => setZoom(value as number)}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button disabled={isUploadingPhoto} onClick={handleClose}>
                {t('actions:cancel')}
              </Button>

              <LoadingButton
                variant="contained"
                disabled={isUploadingPhoto}
                loading={isUploadingPhoto}
                onClick={uploadPhoto}
              >
                {t('actions:save')}
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}
